import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { NotificationTemplatesService } from '../../../../services/notifications/notification-templates-service';
import { NotificationTemplate, TemplateCategory } from '../../../../models/notification-template';

export const TEMPLATE_CATEGORIES: { label: string; value: TemplateCategory }[] = [
  { label: 'Promo', value: 'PROMO' },
  { label: 'Alert', value: 'ALERT' },
  { label: 'Reminder', value: 'REMINDER' },
  { label: 'System', value: 'SYSTEM' },
];

@Component({
  selector: 'app-add-template-form',
  imports: [ReactiveFormsModule, Dialog, InputTextModule, TextareaModule, ButtonModule],
  templateUrl: './add-template-form.html',
  styleUrl: './add-template-form.scss',
})
export class AddTemplateForm {
  private service = inject(NotificationTemplatesService);

  @Output() visibilityChange = new EventEmitter<boolean>();
  @Output() created = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  @Input() editTemplate: NotificationTemplate | null = null;

  @Input()
  get visibility() { return this._visibility; }
  set visibility(value: boolean) {
    this._visibility = value;
    if (value) this.reset();
  }
  private _visibility = false;

  categories = TEMPLATE_CATEGORIES;
  isSubmitted = false;
  loading = false;
  form: FormGroup;

  get isEditMode() { return !!this.editTemplate; }
  get bodyLength() { return (this.form.get('body')?.value ?? '').length; }

  private fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      category: ['PROMO', Validators.required],
      title: ['', Validators.required],
      body: ['', [Validators.required, Validators.maxLength(160)]],
    });
  }

  selectCategory(value: TemplateCategory) {
    this.form.patchValue({ category: value });
  }

  submit() {
    this.isSubmitted = true;
    if (this.form.invalid) return;
    const { name, category, title, body } = this.form.value;
    this.loading = true;

    if (this.isEditMode && this.editTemplate) {
      this.service.updateTemplate(this.editTemplate.id, { name, category, title, body }).subscribe({
        next: () => { this.saved.emit(); this.loading = false; this.close(); },
        error: () => { this.loading = false; },
      });
    } else {
      this.service.createTemplate({ name, category, title, body }).subscribe({
        next: () => { this.created.emit(); this.loading = false; this.close(); },
        error: () => { this.loading = false; },
      });
    }
  }

  cancel() { this.close(); }

  private close() {
    this._visibility = false;
    this.visibilityChange.emit(false);
  }

  private reset() {
    this.isSubmitted = false;
    this.loading = false;
    if (this.editTemplate) {
      this.form.reset({
        name: this.editTemplate.name,
        category: this.editTemplate.category,
        title: this.editTemplate.title,
        body: this.editTemplate.body,
      });
    } else {
      this.form.reset({ category: 'PROMO' });
    }
  }
}
